import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, logout as apiLogout } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have a session token
        const token = localStorage.getItem('ohmnai_session_token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Verify with backend
        const userData = await getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.log('Not authenticated');
        // Clear invalid token
        localStorage.removeItem('ohmnai_session_token');
        localStorage.removeItem('ohmnai_user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login with Google
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Set user after successful auth
  const setAuthUser = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    if (token) {
      localStorage.setItem('ohmnai_session_token', token);
    }
    if (userData) {
      localStorage.setItem('ohmnai_user', JSON.stringify(userData));
    }
  };

  // Logout
  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('ohmnai_session_token');
      localStorage.removeItem('ohmnai_user');
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    loginWithGoogle,
    setAuthUser,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
